import { onAuthStateChanged, signOut as firebaseSignOut } from "firebase/auth";
import type { User } from "firebase/auth";
import React, { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { UserRole } from "../admin/types";

interface AuthState {
  user: User | null;
  role: UserRole | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function initAuth() {
      const { auth } = await import("../../lib/firebase");

      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!isMounted) return;

        if (firebaseUser) {
          const tokenResult = await firebaseUser.getIdTokenResult(true);
          const claimedRole = tokenResult.claims["role"] as UserRole | undefined;
          setUser(firebaseUser);
          setRole(claimedRole ?? null);
        } else {
          setUser(null);
          setRole(null);
        }

        setLoading(false);
      });

      return unsubscribe;
    }

    const unsubscribePromise = initAuth();

    return () => {
      isMounted = false;
      unsubscribePromise.then((unsubscribe) => unsubscribe?.());
    };
  }, []);

  async function signOut() {
    const { auth } = await import("../../lib/firebase");
    await firebaseSignOut(auth);
  }

  return (
    <AuthContext.Provider value={{ user, role, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
